import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { createPortal } from "react-dom";

import { useAuth } from "../contexts/AuthContext";
import { useAbility } from "../contexts/AbilityContext";
import { useReportIssueWidget } from "../contexts/ReportIssueWidgetContext";
import {
  useHasUnreadReportedIssues,
  useHasUnreadTriageIssues,
} from "../features/system-issues/hooks/useSystemIssues";
import { useHasUnreadOccurrences } from "../features/occurrences/hooks/useHasUnreadOccurrences";
import type { AppSubject } from "../types/auth";
import type { AppAction } from "@/types/shared-enums.generated";
import { useIsReadOnlyUser } from "../hooks";
import { routeAccess } from "../router/route-access";
import { gateForPath } from "../router/routes";
import { useOnboardingQueueCount } from "../hooks/useOnboardingQueueCount";
import CanAccess from "./CanAccess";
import {
  LayoutDashboard,
  ListTodo,
  Bell,
  TrendingUp,
  Folder,
  BarChart3,
  FileText,
  HelpCircle,
  LucideIcon,
  FileStack,
  ChevronRight,
  ChevronDown,
  Ship,
  Clock,
  Receipt,
  ScrollText,
  Truck,
  Radar,
  Kanban,
  BookOpen,
  LayoutGrid,
  MessageSquare,
  DollarSign,
  Container,
  ClipboardList,
  CircleDollarSign,
  PieChart,
  Bug,
  ClipboardCheck,
  Calculator,
  ListOrdered,
  Users,
  Timer,
} from "lucide-react";
import { PermissionInput } from "../types/auth";
import type { FeatureKey } from "../types/feature-keys.generated";
import type { GlobalTipoCode } from "../lib/authz";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const SIDEBAR_SECTIONS_STORAGE_KEY = "sidebar:sections:v1";

function loadSectionState(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(SIDEBAR_SECTIONS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveSectionState(state: Record<string, boolean>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SIDEBAR_SECTIONS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / privacy mode */
  }
}

interface SidebarItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Rank mínimo (hierárquico) — espelha o gate equivalente do backend. */
  minRank?: number | GlobalTipoCode;
  permissions?: PermissionInput | PermissionInput[];
  capabilities?:
  | keyof import("../types/auth").UserCapabilities
  | Array<keyof import("../types/auth").UserCapabilities>;
  features?: FeatureKey | FeatureKey[];
  openInNewTab?: boolean;
}

interface SidebarGroup {
  label?: string;
  items: SidebarItem[];
}

const GERAL_ITEMS: SidebarItem[] = [
  { path: "/dashboard", label: "Automações", icon: LayoutDashboard, features: "automacoes" },
  { path: "/equipe", label: "Equipe", icon: Users },
];

// Cada grupo é declarado em ordem alfabética (por label, locale pt-BR).
// Manter essa convenção ao adicionar/renomear itens.

const IMPORTACAO_ITEMS: SidebarItem[] = [
  { path: "/admissoes-temporarias", label: "Admissões Temporárias", icon: Clock, features: "admissoes-temporarias" },
  { path: "/comparacao-catalogo", label: "Catálogo", icon: BookOpen, features: "comparacao-catalogo" },
  { path: "/guia-icms", label: "Guia de ICMS", icon: Receipt, features: "guia-icms" },
  { path: "/ce-cct", label: "CE / CCT", icon: Container, features: "ce-cct" },
  { path: "/demurrage", label: "Demurrage", icon: Timer, features: "demurrage" },
  { path: "/licencas", label: "LPCOs", icon: ScrollText, permissions: { action: "read", subject: "Catalogo" }, features: "licencas" },
  { path: "/notificacoes-aduaneiras", label: "Notificações Aduaneiras", icon: Bell, features: "notificacoes-aduaneiras" },
  { path: "/comparacao-documentos", label: "Novo Processo", icon: FileStack, permissions: { action: "read", subject: "Process" }, features: "comparacao-documentos" },
  { path: "/occurrences", label: "Ocorrências", icon: MessageSquare, capabilities: "canAccessOccurrences", features: "occurrences", openInNewTab: true },
  { path: "/painel-aduaneiro", label: "Painel de Controle", icon: TrendingUp, features: "painel-aduaneiro" },
  { path: "/pos-registro", label: "Pós-Registro", icon: FileText, features: "pos-registro" },
  { path: "/purchase-orders", label: "Purchase Orders", icon: ListOrdered, permissions: { action: "read", subject: "PurchaseOrder" }, features: "purchase-orders" },
  { path: "/simulacoes-importacao", label: "Simulação de Importação", icon: Calculator, permissions: { action: "read", subject: "ImportSimulation" }, features: "simulacao-importacao" },
  { path: "/tracking", label: "Tracking", icon: Radar, permissions: { action: "read", subject: "Process" } },
];

const OPERACAO_ITEMS: SidebarItem[] = [
  { path: "/cotacoes", label: "Cotações", icon: ClipboardList, permissions: { action: "read", subject: "Quotation" } },
  { path: "/instrucoes-processo", label: "Instruções de Processo", icon: BookOpen, features: "instrucoes-processo" },
  { path: "/cotacoes-positivo", label: "Cotações Positivo", icon: DollarSign, features: "cotacoes-positivo" },
  { path: "/exportacao", label: "Exportação", icon: Ship, features: "exportacao" },
  {
    path: "/fila-melhorias",
    label: "Fila de Solicitações",
    icon: ListTodo,
    permissions: { action: "read", subject: "Solicitacao" },
    features: "solicitacoes",
  },
  { path: "/macros", label: "Macros", icon: Folder, features: "macros" },
  { path: "/powerbi", label: "Power BI", icon: BarChart3, capabilities: "canViewPowerBI" },
  {
    path: "/registro-onboarding",
    label: "Registro de Onboarding",
    icon: ClipboardCheck,
    minRank: "supervisor",
  },
  // Sem `roles`: a feature é rankConfigurable e o backend já aplica o nível
  // mínimo do CC ao montar enabled_features — ver a rota em router/routes.ts.
  { path: "/transportadoras", label: "Transportadoras", icon: Truck, features: "transportadoras" },
];

// Itens dev-only consolidados no Painel /admin (cards): Tipos de Processo,
// Gerenciar Automações, Execuções/Status das Automações, Gerenciar Jobs,
// Logs de Acesso. Mantidos como rotas, fora da sidebar.
const DEV_ITEMS: SidebarItem[] = [
  { path: "/admin", label: "Painel Admin", icon: LayoutGrid, capabilities: "canManageDevTools" },
  { path: "/system-issues/triage", label: "Problemas Reportados", icon: Bug, capabilities: "canManageDevTools" },
];

const FINANCEIRO_ITEMS: SidebarItem[] = [
  { path: "/painel-financeiro", label: "Painel Financeiro", icon: CircleDollarSign, features: "painel-financeiro" },
  { path: "/relatorios-financeiros", label: "Relatórios", icon: PieChart, features: "painel-financeiro.relatorios" },
];

const PAINEIS_ITEMS: SidebarItem[] = [
  { path: "/paineis/launch", label: "Dashboards", icon: BarChart3 },
];

const FOOTER_ITEMS: SidebarItem[] = [
  { path: "/ajuda", label: "Ajuda", icon: HelpCircle },
  { path: "/planner", label: "Planner", icon: Kanban, capabilities: "canAccessPlanner", openInNewTab: true },
];

// Escondidas quando a empresa desliga a feature 'menu-completo'.
const RESTRICTED_MENU_HIDDEN_PATHS = [
  "/dashboard",
  "/fila-melhorias",
  "/notificacoes-aduaneiras",
  "/admissoes-temporarias",
  "/macros",
  "/powerbi",
  "/exportacao",
];

// NavLink casa por descendência: o item mais específico ganha; a rota de detalhe
// (/purchase-orders/123) acende o item pai.
const ALL_SIDEBAR_PATHS = [
  ...GERAL_ITEMS,
  ...IMPORTACAO_ITEMS,
  ...OPERACAO_ITEMS,
  ...DEV_ITEMS,
  ...PAINEIS_ITEMS,
  ...FOOTER_ITEMS,
].map((i) => i.path);

function resolveActivePath(pathname: string): string | null {
  return (
    ALL_SIDEBAR_PATHS.filter(
      (p) => pathname === p || pathname.startsWith(`${p}/`)
    ).sort((a, b) => b.length - a.length)[0] ?? null
  );
}

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

type HoverTooltipProps = {
  label: string;
  disabled?: boolean;
  side?: "right" | "left";
  children: React.ReactElement;
};

function HoverTooltip({ label, disabled, side = "right", children }: HoverTooltipProps) {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const closeTimer = React.useRef<number | null>(null);

  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const anchorRef = React.useRef<HTMLElement | null>(null);

  const updatePosition = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 14;
    const top = r.top + r.height / 2;
    const left = side === "right" ? r.right + gap : r.left - gap;
    setPos({ top, left });
  }, [side]);

  React.useEffect(() => {
    if (open) {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
      setMounted(true);
      updatePosition();
      return;
    }
    if (mounted) {
      closeTimer.current = window.setTimeout(() => setMounted(false), 160);
    }
    return () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, [open, mounted, updatePosition]);

  React.useEffect(() => {
    if (!mounted) return;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [mounted, updatePosition]);

  const child = React.cloneElement(children, {
    ref: (node: HTMLElement) => {
      anchorRef.current = node;
      const originalRef = (children as any).ref;
      if (typeof originalRef === "function") originalRef(node);
      else if (originalRef && typeof originalRef === "object") originalRef.current = node;
    },
    onMouseEnter: (e: React.MouseEvent) => {
      children.props.onMouseEnter?.(e);
      if (disabled) return;
      setOpen(true);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      children.props.onMouseLeave?.(e);
      setOpen(false);
    },
    onFocus: (e: React.FocusEvent) => {
      children.props.onFocus?.(e);
      if (disabled) return;
      setOpen(true);
    },
    onBlur: (e: React.FocusEvent) => {
      children.props.onBlur?.(e);
      setOpen(false);
    },
  });

  return (
    <>
      {child}
      {mounted &&
        !disabled &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={cn(
              "fixed z-[9999] pointer-events-none",
              "transition-[opacity,transform] duration-150 ease-out",
              open ? "opacity-100" : "opacity-0"
            )}
            style={{
              top: pos.top,
              left: pos.left,
              transform:
                side === "right"
                  ? open
                    ? "translateY(-50%) translateX(0)"
                    : "translateY(-50%) translateX(-4px)"
                  : open
                    ? "translate(-100%, -50%) translateX(0)"
                    : "translate(-100%, -50%) translateX(4px)",
            }}
          >
            <div className="rounded-md bg-zinc-900/95 text-white text-xs font-medium px-2 py-1 shadow-lg border border-white/10 backdrop-blur whitespace-nowrap">
              {label}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const location = useLocation();
  const { capabilities, hasRank, hasCap, isFeatureEnabled } = useAuth();
  const { ability } = useAbility();
  const { openWidget: openReportIssueWidget } = useReportIssueWidget();
  const isReadOnlyRoleHook = useIsReadOnlyUser();

  const isDeveloper = capabilities.canManageDevTools;
  const onboardingCount = useOnboardingQueueCount(isDeveloper);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => loadSectionState());
  const SECTION_DEFAULTS: Record<string, boolean> = {
    "Importação": true,
    "Operação": true,
  };
  const isSectionOpen = (label: string) =>
    openSections[label] ?? SECTION_DEFAULTS[label] ?? false;
  const toggleSection = (label: string) => {
    setOpenSections((prev) => {
      const current = prev[label] ?? SECTION_DEFAULTS[label] ?? false;
      const next = { ...prev, [label]: !current };
      saveSectionState(next);
      return next;
    });
  };

  const isReadOnlyRole = isReadOnlyRoleHook;

  // Bolinha vermelha nos itens de "Reportar Problema" — mesma fonte de
  // dados (`unread_count` por issue) que alimenta a badge estilo WhatsApp
  // nos cards de `MyIssuesList`/`TriageQueueList`. A da fila de triagem só
  // é buscada pra quem de fato vê esse item (dev que edita).
  const hasUnreadReported = useHasUnreadReportedIssues();
  const hasUnreadTriage = useHasUnreadTriageIssues(isDeveloper && !isReadOnlyRole);

  // Mesma bolinha, agora no item "Ocorrências": acende quando há mensagem
  // não lida em qualquer processo do escopo do usuário. Só consulta quem tem
  // a capability (a rota é gated por ela + feature `occurrences`).
  const hasUnreadOccurrences = useHasUnreadOccurrences(
    capabilities.canAccessOccurrences === true,
  );

  const accessCtx = {
    hasRank, hasCap, capabilities, isFeatureEnabled,
    can: (a: AppAction, s: AppSubject) => ability.can(a, s),
  };
  /** Item só aparece se a rota dele abriria — mesma regra do ProtectedRoute. */
  const routeOk = (path: string) => {
    const gate = gateForPath(path);
    return gate ? routeAccess(gate, accessCtx) === 'ok' : true;
  };

  const isRestrictedMenu = !isFeatureEnabled("menu-completo");

  const canShowPowerBI = (path: string) =>
    path === "/powerbi" && capabilities.canViewPowerBI;

  /**
   * Replica a lógica do <CanAccess> para um SidebarItem. Sem isso, o filtro
   * de roles/permissions/capabilities/features só seria aplicado no render
   * de cada item — e categorias inteiras (ex.: "Operação") apareceriam
   * como header órfão quando o usuário não tem acesso a nenhum item dela.
   */
  const hasAccessToItem = (item: SidebarItem): boolean => {
    if (item.minRank !== undefined) {
      if (!hasRank(item.minRank)) return false;
    }
    if (item.permissions) {
      const arr = Array.isArray(item.permissions) ? item.permissions : [item.permissions];
      const ok = arr.every((p) => {
        if (typeof p === "string") {
          const [action, subject] = p.split(":") as [AppAction, AppSubject];
          return ability.can(action, subject);
        }
        return ability.can(p.action, p.subject);
      });
      if (!ok) return false;
    }
    if (item.capabilities && capabilities) {
      const arr = Array.isArray(item.capabilities) ? item.capabilities : [item.capabilities];
      const ok = arr.every((c) => capabilities[c] === true);
      if (!ok) return false;
    }
    if (item.features) {
      const arr = Array.isArray(item.features) ? item.features : [item.features];
      const ok = arr.every((k) => isFeatureEnabled(k));
      if (!ok) return false;
    }
    return true;
  };

  const filterItems = (items: SidebarItem[]): SidebarItem[] => {
    let result = items;
    if (isRestrictedMenu) {
      result = result.filter(
        (i) => !RESTRICTED_MENU_HIDDEN_PATHS.includes(i.path) || canShowPowerBI(i.path),
      );
    }
    // Filtra por roles/permissions/capabilities/features para que grupos
    // sem nenhum item visível sejam descartados pela checagem
    // `group.items.length === 0` no render. `routeOk` aplica a exigência da
    // rota (capability + confinamento de leitura) — item que levaria a
    // /access-denied ou de volta ao painel não aparece.
    result = result.filter((i) => hasAccessToItem(i) && routeOk(i.path));
    return result;
  };

  const groups: SidebarGroup[] = [
    { items: filterItems(GERAL_ITEMS) },
    { label: "Importação", items: filterItems(IMPORTACAO_ITEMS) },
    { label: "Operação", items: filterItems(OPERACAO_ITEMS) },
  ];

  // Financeiro: `can_access_financial_panel` (gate da rota, via routeOk) + feature
  // do item; grupo vazio é descartado no render.
  groups.push({ label: "Financeiro", items: filterItems(FINANCEIRO_ITEMS) });
  groups.push({ items: filterItems(PAINEIS_ITEMS) });

  if (isDeveloper && !isReadOnlyRole) {
    groups.push({ label: "Ferramentas Dev", items: DEV_ITEMS });
  }

  useEffect(() => {
    const w = isOpen ? 224 : 60;
    document.documentElement.style.setProperty("--sidebar-w", `${w}px`);
    return () => {
      document.documentElement.style.removeProperty("--sidebar-w");
    };
  }, [isOpen]);

  const itemBase =
    "w-full flex items-center gap-2.5 rounded-md text-[13px] font-medium relative transition-colors duration-150";
  const itemPad = isOpen ? "px-3 py-1.5" : "px-2 py-1.5 justify-center";
  const itemInactive = "text-zinc-400 hover:bg-surface/5 hover:text-white";
  const itemActive = "bg-surface/[0.08] text-white";
  const iconClass = "w-[18px] h-[18px] flex-shrink-0";

  const activePath = resolveActivePath(location.pathname);

  /** Bolinha de notificação — mesma posição/estilo nos dois lugares que a
   * usam (item de nav genérico e o botão bespoke de "Relatar um problema"). */
  const notificationDot = (
    <span
      className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-zinc-950"
      aria-hidden
    />
  );

  const renderNavItem = (item: SidebarItem, showDot = false) => {
    // Mesmo badge nos dois ramos de render (NavLink e âncora externa).
    const badgeOnboarding = (it: SidebarItem) =>
      isOpen && it.path === "/admin" && onboardingCount > 0 ? (
        <span className="ml-auto rounded-full bg-blue-600 px-1.5 text-[10px] font-medium text-white">
          {onboardingCount}
        </span>
      ) : null;

    const inner = ({ isActive }: { isActive: boolean }) => (
      <>
        {isActive && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-blue-500" />
        )}
        <span className="relative inline-flex shrink-0">
          <item.icon className={iconClass} />
          {showDot && notificationDot}
        </span>
        {isOpen && <span className="truncate">{item.label}</span>}
        {badgeOnboarding(item)}
      </>
    );

    const navLinkClass = ({ isActive }: { isActive: boolean }) =>
      cn(itemBase, itemPad, isActive ? itemActive : itemInactive);

    const externalAnchor = (
      <a
        href={item.path}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(itemBase, itemPad, itemInactive)}
      >
        <span className="relative inline-flex shrink-0">
          <item.icon className={iconClass} />
          {showDot && notificationDot}
        </span>
        {isOpen && <span className="truncate">{item.label}</span>}
        {badgeOnboarding(item)}
      </a>
    );

    const isExternalLink = item.path.startsWith("http") || item.openInNewTab;

    const isActive = item.path === activePath;

    const content = isExternalLink ? externalAnchor : (
      <NavLink to={item.path} className={() => navLinkClass({ isActive })}>
        {inner({ isActive })}
      </NavLink>
    );

    return !isOpen ? (
      <HoverTooltip key={item.path} label={item.label}>
        {content}
      </HoverTooltip>
    ) : (
      <React.Fragment key={item.path}>{content}</React.Fragment>
    );
  };

  /**
   * Sem allowlist/role gate de propósito — "Relatar um problema" precisa
   * estar sempre acessível, de qualquer lugar do app, pra qualquer usuário
   * autenticado (mesmo espírito do antigo botão flutuante que este item
   * substituiu). Abre o drawer via `ReportIssueWidgetContext` em vez de
   * navegar — não é uma rota, é uma ação.
   */
  const renderReportIssueButton = () => {
    const button = (
      <button
        type="button"
        onClick={openReportIssueWidget}
        title="Reportar um problema do sistema"
        className={cn(itemBase, itemPad, itemInactive)}
      >
        <span className="relative inline-flex shrink-0">
          <Bug className={iconClass} />
          {hasUnreadReported && notificationDot}
        </span>
        {isOpen && <span className="truncate">Relatar um problema</span>}
      </button>
    );

    return !isOpen ? (
      <HoverTooltip key="report-issue" label="Relatar um problema">
        {button}
      </HoverTooltip>
    ) : (
      <React.Fragment key="report-issue">{button}</React.Fragment>
    );
  };

  const footerItems = isReadOnlyRole ? [] : FOOTER_ITEMS;

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
          role="presentation"
        />
      )}

      <aside
        className={cn(
          "fixed top-0 left-0 z-50 lg:relative lg:z-auto",
          "flex flex-col h-screen shadow-xl",
          "bg-zinc-950 text-white border-r border-white/10",
          "overflow-x-hidden",
          "transition-[width] duration-300 ease-in-out",
          isOpen ? "w-56" : "w-[60px]",
          "lg:h-screen lg:flex-shrink-0"
        )}
      >
        <div className="h-20 border-b border-white/10 px-2 flex items-center justify-center flex-shrink-0">
          {isOpen ? (
            <button
              onClick={onToggle}
              className="w-full h-full flex items-center justify-center transition-opacity hover:opacity-90"
              title="Recolher menu"
              type="button"
            >
              <img
                src="/logo-codifica.png"
                alt="Logo Codifica"
                style={{ width: 180, height: 32 }}
              />
            </button>
          ) : (
            <HoverTooltip label="Abrir menu">
              <button
                onClick={onToggle}
                className="w-full h-full flex items-center justify-center transition-opacity hover:opacity-90"
                aria-label="Abrir menu"
                type="button"
              >
                <div className="w-9 h-9 rounded-md bg-surface/5 hover:bg-surface/10 flex items-center justify-center border border-white/10">
                  <ChevronRight className="w-4 h-4 text-zinc-200" />
                </div>
              </button>
            </HoverTooltip>
          )}
        </div>

        <nav className="sidebar-scroll flex-1 py-2 px-2 overflow-y-auto overflow-x-hidden">
          {groups.map((group, idx) => {
            if (group.items.length === 0) return null;

            const collapsible = isOpen && !!group.label;
            const expanded = collapsible ? isSectionOpen(group.label!) : true;
            const itemsVisible = !isOpen || !group.label || expanded;

            return (
              <div key={group.label ?? `group-${idx}`} className={idx === 0 ? "" : "mt-2"}>
                {collapsible ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(group.label!)}
                    aria-expanded={expanded}
                    className="w-full px-3 mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    <span className="truncate">{group.label}</span>
                    <ChevronDown
                      className={cn(
                        "ml-auto w-3 h-3 transition-transform duration-200",
                        expanded ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </button>
                ) : (
                  !isOpen && idx !== 0 && <div className="mx-2 mb-1 h-px bg-surface/5" />
                )}
                {itemsVisible && (
                  <div className="flex flex-col gap-0.5">
                    {group.items.map((item) => (
                      <CanAccess
                        key={item.path}
                        minRank={item.minRank}
                        permissions={item.permissions}
                        capabilities={item.capabilities}
                        features={item.features}
                      >
                        {renderNavItem(
                          item,
                          (item.path === "/system-issues/triage" &&
                            hasUnreadTriage) ||
                            (item.path === "/occurrences" &&
                              hasUnreadOccurrences),
                        )}
                      </CanAccess>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="px-2 pb-2 flex-shrink-0">
          <div className="h-px bg-surface/10 my-1" />
          <div className="flex flex-col gap-0.5">
            {renderReportIssueButton()}
            {footerItems.map((item) => (
              <CanAccess
                key={item.path}
                minRank={item.minRank}
                permissions={item.permissions}
                capabilities={item.capabilities}
                features={item.features}
              >
                {renderNavItem(item)}
              </CanAccess>
            ))}
          </div>
        </div>
      </aside>

      <style>{`
        .sidebar-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.18) transparent;
        }
        .sidebar-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
          margin: 4px 0;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.14);
          border-radius: 999px;
          border: 2px solid transparent;
          background-clip: padding-box;
          min-height: 40px;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.24);
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        .sidebar-scroll::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </>
  );
};

export default Sidebar;
